import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqualWith'
import isFunction from 'lodash/isFunction'

import ConfigProcessor from './processor'

import { checkChartConfig } from './utils'
import F2 from '@antv/f2/lib'
import '@antv/f2/lib/interaction/pan'
import '@antv/f2/lib/interaction/pinch'
import '@antv/f2/lib/interaction/pie-select'

const Gesture = require('@antv/f2/lib/plugin/gesture')

import PieLabel from '../../plugins/PieLabel' //require('@antv/f2/lib/plugin/pie-label')

import registerPlugin from '../../plugins'
import chartDetailLegend from '../../plugins/ChartDetailLegend'

F2.Chart.plugins.register(Gesture)
F2.Chart.plugins.register(PieLabel)
registerPlugin(F2)

export default class F2Chart {
  chartInstance = null
  config = null
  oriConfig = null

  constructor(config, updateLegends, updateTooltipEl) {
    this.config = cloneDeep(config)
    checkChartConfig(this.config)
    this.chartInstance = new F2.Chart(this.config.chart)
    this.updateTooltipEl = updateTooltipEl

    // 注册详情页 legend 更新
    this.chartInstance.registerPlugins(chartDetailLegend)
    this.chartInstance.setLegendUpdater(updateLegends)
    this.chartInstance.animate({
      'axis-label': {
        enter: {
          animation: 'fadeIn',
        },
        update: {
          animation: 'fadeIn',
        },
        leave: {
          animation: 'fadeOut'
        },
      },
      'axis-tick': {
        enter: {
          animation: 'fadeIn',
        },
        update: {
          animation: 'fadeIn',
        },
      },
      'axis-grid': {
        enter: {
          animation: 'fadeIn',
        },
        update: {
          animation: 'fadeIn',
        },
      },
    })
  }

  render() {
    const { config } = this
    const chart = this.chartInstance
    F2Chart.setData(chart, config)
    F2Chart.setAxis(chart, config)
    F2Chart.setCoord(chart, config)
    F2Chart.setGuide(chart, config)
    F2Chart.setSeries(chart, config)
    F2Chart.setLegend(chart, config)
    F2Chart.setAnimate(chart, config)
    F2Chart.setTooltip(chart, config)
    F2Chart.setPieLabel(chart, config)
    this.oriConfig = config
    chart.render()
    this.renderDefaultTooltip(chart, config)
  }

  repaint(config, tooltip = null) {
    const newConfig = cloneDeep(config)
    checkChartConfig(newConfig)
    this.renderDiffConfig(newConfig)
    this.oriConfig = newConfig

    if (tooltip) {
      this.chartInstance.showTooltip(tooltip)
    } else {
      this.renderDefaultTooltip(this.chartInstance, newConfig)
    }
  }

  destroy(chart) {
    const chartInstance = chart || this.chartInstance
    this.updateTooltipEl = null
    this.oriConfig = null
    this.config = null
    if (!chartInstance) return

    chartInstance.destroy()
  }

  clear(chart) {
    chart && chart.clear()
  }

  static setData(chart, config) {
    return ConfigProcessor.setDataConfig.process(chart, config)
  }

  static setCoord(chart, config) {
    return ConfigProcessor.setCoordConfig.process(chart, config)
  }

  static setGuide(chart, config) {
    return ConfigProcessor.setGuideConfig.process(chart, config)
  }

  static setAxis(chart, config) {
    return ConfigProcessor.setAxisConfig.process(chart, config)
  }

  static setSeries(chart, config) {
    return ConfigProcessor.setSeriesConfig.process(chart, config)
  }

  static setAnimate(chart, config) {
    return ConfigProcessor.setAnimateConfig.process(chart, config)
  }

  static setLegend(chart, config) {
    return ConfigProcessor.setLegendConfig.process(chart, config)
  }

  static setTooltip(chart, config) {
    return ConfigProcessor.setTooltipConfig.process(chart, config)
  }

  static setPieLabel(chart, config) {
    return ConfigProcessor.setPieLabelConfig.process(chart, config)
  }

  repaintContent(chart, config) {
    F2Chart.setData(chart, config)
    F2Chart.setAxis(chart, config)
    F2Chart.setCoord(chart, config)
    F2Chart.setGuide(chart, config)
    F2Chart.setSeries(chart, config)
    F2Chart.setLegend(chart, config)
    F2Chart.setAnimate(chart, config)
    F2Chart.setTooltip(chart, config)
    F2Chart.setPieLabel(chart, config)
  }

  renderDiffConfig(config) {
    const { oriConfig } = this
    const chart = this.chartInstance

    const hasContentChange = !isEqual(oriConfig, config)

    if (hasContentChange) {
      chart.clear()

      if (!isEqual(oriConfig.chart.appendPadding, config.chart.appendPadding)) {
        chart.set('appendPadding', config.chart.appendPadding)
      }

      if (!isEqual(oriConfig.chart.padding, config.chart.padding)) {
        chart.set('padding', config.chart.padding)
      }

      if (!isEqual(oriConfig.chart.syncY, config.chart.syncY)) {
        chart.set('syncY', config.chart.syncY)
      }

      this.repaintContent(chart, config)
      // 关闭轴标签动画，编辑器时会导致不及时更新
      chart.animate({
        'axis-label': false,
      })
      chart.repaint()
    }
  }

  renderDefaultTooltip(chart, config) {
    const cTooltip = cloneDeep(config.tooltip)
    if (cTooltip && cTooltip.show && cTooltip.defaultItem) {
      const point = chart.getPosition(cTooltip.defaultItem)
      chart.showTooltip(point)
    }
  }

  /**
   * resize 单图
   */
  resize = () => {
    if (!this.chartInstance) return
    const { chart } = this.config || {}
    let chartEl

    if (!chart) return

    if (chart.id) {
      chartEl = document.getElementById(chart.id)
    } else if (chart.el) {
      chartEl = chart.el
    } else {
      throw new Error('please set correct chart option')
    }

    const relativeWidth = chartEl.parentElement.clientWidth
    const relativeHeight = chartEl.parentElement.clientHeight

    // 重置缩放插件状态
    if (this.chartInstance.resetElasticPainter) {
      this.chartInstance.resetElasticPainter()
    }

    this.chartInstance.changeSize(relativeWidth, relativeHeight)

    if (isFunction(this.updateTooltipEl)) {
      this.updateTooltipEl()
    }
  }

  runDrawGraph = () => {
    if (this.chartInstance) {
      this.resize()
    }
  }
}
